import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { FlowService } from "@gen/tracker/v1/flow_connect";
import { Flow } from "@gen/tracker/v1/flow_pb";
import { FieldError, ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import { useByIds } from "@/lib/services/catalog";

const client = createClient(FlowService);

export const flowKeys = {
  all: ["flows"] as const,
  list: (params: ListFlowsParams) => [...flowKeys.all, "list", params] as const,
  detail: (id: string) => [...flowKeys.all, "detail", id] as const,
};

export interface ListFlowsParams {
  search?: string;
  ids?: string[];
  /** name | updated_at | created_at; none = newest first. */
  sort?: ListSort;
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export function useListFlows(
  params: ListFlowsParams = {},
  options?: Partial<UseQueryOptions<{ items: Flow[]; total: number }>>,
) {
  return useQuery({
    queryKey: flowKeys.list(params),
    queryFn: async () => {
      const res = await client.listFlows({
        search: params.search ?? "",
        ids: params.ids ?? [],
        sort: params.sort,
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return { items: res.items, total: res.pagination?.total ?? res.items.length };
    },
    ...options,
  });
}

/** The flows with these ids, by id — archived ones too. */
export function useFlowsByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<Flow>(
    flowKeys.all,
    ids,
    async (chunk) => (await client.listFlows({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

export function useGetFlow(id: string | undefined, options?: Partial<UseQueryOptions<Flow | undefined>>) {
  return useQuery({
    queryKey: flowKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getFlow({ id });
      return res.flow;
    },
    ...options,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flow: Flow) => {
      const res = await client.createFlow({ flow });
      return res.flow!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowKeys.all });
    },
  });
}

// useQuickStartFlow mints a simple starter graph (prelander? → landing →
// offer, mode SINGLE_OFFER) via the QuickStartFlow RPC and persists it. This
// is the successor to the retired campaign-managed template generation — the
// result is a NORMAL, fully-editable flow the author then shapes in the visual
// builder. landerId + offerId are required; prelanderId is optional.
export function useQuickStartFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      landerId: string;
      offerId: string;
      prelanderId?: string;
    }) => {
      // workspace_id is resolved server-side from the caller's tenant — never
      // sent by the client (an empty value fails protovalidate).
      const res = await client.quickStartFlow({
        name: input.name,
        landerId: input.landerId,
        offerId: input.offerId,
        prelanderId: input.prelanderId ?? "",
      });
      return res.flow!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowKeys.all });
    },
  });
}

export function useUpdateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flow: Flow) => {
      const res = await client.updateFlow({ flow });
      return res.flow!;
    },
    onSuccess: (flow) => {
      void qc.invalidateQueries({ queryKey: flowKeys.all });
      qc.setQueryData(flowKeys.detail(flow.id), flow);
    },
  });
}

// useValidateFlow runs the BE ValidateFlow RPC against the in-flight draft.
// The hook is the source of truth for inline error painting in the editor —
// canvas badges, inspector field errors, and the save-disabled state all
// derive from `errors`. The query uses `enabled` as a manual gate so the
// editor can pace calls (debounce 300ms via the page) and avoid hammering
// the server on every keystroke.
export function useValidateFlow(
  flow: Flow | null,
  enabled: boolean,
): { errors: FieldError[]; isLoading: boolean; refetch: () => void } {
  const query = useQuery({
    queryKey: ["flow-validate", flow ? flow.toJsonString() : null],
    enabled: enabled && !!flow,
    queryFn: async () => {
      if (!flow) return [] as FieldError[];
      const res = await client.validateFlow({ flow });
      return res.errors;
    },
    staleTime: 1000,
  });
  return {
    errors: query.data ?? [],
    isLoading: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

export function useArchiveFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveFlow({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowKeys.all });
    },
  });
}

// useSuggestFlow drafts a routing graph from a plain-English funnel
// description (SuggestFlow RPC, OpenRouter-backed). The draft is applied as an
// editor draft — never persisted directly — so the assistant returns the Flow
// plus a rationale and any validation errors that will paint inline on apply.
export function useSuggestFlow() {
  return useMutation({
    mutationFn: async (description: string) => {
      const res = await client.suggestFlow({ description });
      return {
        flow: res.flow,
        rationale: res.rationale,
        errors: res.errors as FieldError[],
      };
    },
  });
}
